import bowser from 'yola-bowser';
import classNames from 'classnames';
import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { utils, i18next, linkEditing } from '@yola/ws-sdk';
import { Modal, Panel, PanelGroup, reactHookForm, designSystem } from '@yola/ws-ui';
import constants from '../constants';
import isOpenAddress from '../verifiers/is-open-address';
import LinkSelectorField from './link-selector-field';
import getDownloadOptions from '../helpers/get-download-options';

const { useFormContext, useWatch } = reactHookForm;

const {
  linkSettings: { DIALOG_WIDTH, DIALOG_HEIGHT, PANEL_HEIGHT, INPUTS_IDS, FIELD_NAMES },
  common: { ENTER_KEYCODE },
} = constants;

const { linkTypes } = linkEditing.constants;
const {
  ActionButton,
  Paragraph,
  Heading,
  Box,
  Stack,
  Loader,
  Mark,
  ControlGroup,
  DialogButtonGroup,
  DialogButtonGroupSeparator,
  DialogButton,
  CheckboxField,
  RadioButtonGroupField,
  InputGroupField,
  FieldStatus,
} = designSystem;

const defaultModalAttributes = {};
const defaultDialogPosition = {
  offsetX: null,
  offsetY: null,
};

const LinkSettingDialog = ({
  latestAnchorReference = '',
  modalAttributes = defaultModalAttributes,
  showTitle = true,
  showDeleteButton = false,
  handleDeleteLink = utils.noop,
  captions,
  panelHeight = `calc(100% - ${PANEL_HEIGHT}px * 2)`,
  handleCancel,
  setIsOverlayClicked = utils.noop,
  dialogPosition = defaultDialogPosition,
  handleSubmit,
  linkSelectorOptions,
  linkSelectorCaptions,
  handleLinkChange = utils.noop,
  handleTargetBlankChange = utils.noop,
  handleNoFollowChange = utils.noop,
  handleUploadFile = utils.noop,
  handleDownloadOption = utils.noop,
  validateLink = utils.noop,
  overlay = 'transparent',
  centered = null,
  appearance = constants.common.DIALOG_APPEARANCE,
  isExternalLinksDisabled = false,
  isExternalLinksUnresolved = false,
  onUpgradeLinksClick = utils.noop,
  onInputClick = utils.noop,
  customPathIsLoading = false,
  customPathDisabled = true,
  toggleCustomPathField = utils.noop,
  onCustomPathChange = utils.noop,
  pathValidation = utils.noop,
}) => {
  const {
    control,
    resetField,
    handleSubmit: manageSubmit,
    formState: { errors },
  } = useFormContext();
  const [linkType, customPathVisible] = useWatch({
    control,
    name: [FIELD_NAMES.linkType, FIELD_NAMES.customPathSwitcher],
  });

  const modalRef = useRef(null);
  const isDialog = appearance === constants.common.DIALOG_APPEARANCE;

  const isExternalLink = linkType === linkTypes.EXTERNAL;
  const isLinkSelectorDisabled = isExternalLink && isExternalLinksDisabled;
  const isLinkSelectorFieldDisabled =
    isLinkSelectorDisabled || (isExternalLink && isExternalLinksUnresolved);
  const downloadOptions = getDownloadOptions();

  const onCancel = () => {
    if (!isDialog) return;

    const {
      state: { offsetX, offsetY },
    } = modalRef.current;

    handleCancel({ offsetX, offsetY });
  };

  const onFileUpload = (newFile) => {
    resetField(FIELD_NAMES.reference);
    handleUploadFile(newFile);
  };

  const onSubmit = (data) => {
    if (!isDialog) return;

    const {
      state: { offsetX, offsetY },
    } = modalRef.current;

    handleSubmit(offsetX, offsetY, data);
  };

  const handleKeyDown = (event) => {
    if (!isDialog) return;

    if (event.keyCode === ENTER_KEYCODE && !isLinkSelectorDisabled) {
      manageSubmit(onSubmit);
    }
  };

  const linkEditorJSX = (
    <div className="ws-modal__block">
      <Box marginTop="spacing-3-xs">
        <ControlGroup title={captions.actionTitle} description={captions.actionSubtitle}>
          <LinkSelectorField
            control={control}
            validateLink={validateLink}
            onClick={onInputClick}
            latestAnchorReference={latestAnchorReference}
            onChange={handleLinkChange}
            onFileUpload={onFileUpload}
            options={linkSelectorOptions}
            captions={linkSelectorCaptions}
            linkTypes={linkTypes}
            disabled={isLinkSelectorFieldDisabled}
          />
        </ControlGroup>
      </Box>
      {isOpenAddress(linkType) && !isLinkSelectorDisabled && (
        <Box marginTop="spacing-2-xs">
          <Stack gap="spacing-2-xs">
            <CheckboxField
              name={FIELD_NAMES.targetBlank}
              control={control}
              label={i18next.t('Open in a new tab')}
              onChange={handleTargetBlankChange}
            />
            <CheckboxField
              name={FIELD_NAMES.nofollowControl}
              control={control}
              label={i18next.t('Add a Nofollow tag')}
              onChange={handleNoFollowChange}
              tooltipProps={{
                id: 'nofollow-control',
                placement: 'bottom',
                text: i18next.t(
                  'The Nofollow tag tells search engines to ignore this link so that it does not impact search engine rank for the target website'
                ),
              }}
            />
          </Stack>
        </Box>
      )}
      {linkType === linkTypes.DOWNLOAD && (
        <Box marginTop="spacing-2-xs">
          <RadioButtonGroupField
            name={FIELD_NAMES.download}
            control={control}
            options={downloadOptions}
            onChange={handleDownloadOption}
          />

          {customPathIsLoading && (
            <Box marginTop="spacing-m">
              <Loader size="medium" />
            </Box>
          )}

          {!customPathIsLoading && (
            <Box marginTop="spacing-xs">
              <Stack gap="spacing-3-xs">
                <CheckboxField
                  name={FIELD_NAMES.customPathSwitcher}
                  control={control}
                  label={i18next.t('Set Download URL')}
                  onChange={toggleCustomPathField}
                  disabled={customPathDisabled}
                />
                {customPathVisible && (
                  <Stack gap="spacing-3-xs">
                    <div>
                      <InputGroupField
                        name={FIELD_NAMES.customPath}
                        control={control}
                        rules={{
                          validate: pathValidation,
                        }}
                        onChange={onCustomPathChange}
                        iconGlyph="link"
                        disabled={customPathDisabled}
                      />
                      {!errors[FIELD_NAMES.customPath] && (
                        <FieldStatus
                          text={
                            <Fragment>
                              {`${i18next.t('The URL will look like this:')} `}

                              <span style={{ whiteSpace: 'nowrap' }}>
                                yourdomain.com
                                <strong>
                                  <Mark appearance="high-emphasis">/your-url</Mark>
                                </strong>
                                .
                              </span>

                              {i18next.t('To publish under a different URL, re-upload the file')}
                            </Fragment>
                          }
                        />
                      )}
                    </div>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      )}
      {isLinkSelectorDisabled && (
        <React.Fragment>
          <div className="ws-link-edit-modal__upgrade-text">
            <Paragraph>
              {i18next.t(
                'This feature is available in Premium plan. Upgrade and get more for your website.'
              )}
            </Paragraph>
          </div>
          <ActionButton
            iconGlyph="upgrade"
            iconPlacement="before"
            format="solid"
            appearance="cta"
            label={i18next.t('Upgrade')}
            onClick={onUpgradeLinksClick}
          />
        </React.Fragment>
      )}
    </div>
  );

  if (appearance === 'dialog-block') {
    return linkEditorJSX;
  }

  return (
    <Modal
      modalRef={modalRef}
      handleCancel={onCancel}
      handleSubmit={() => {
        if (isLinkSelectorDisabled) {
          onCancel();
          return;
        }

        setIsOverlayClicked(true);
        manageSubmit(onSubmit)();
      }}
      resizable={false}
      width={DIALOG_WIDTH}
      maxHeight={DIALOG_HEIGHT}
      height="auto"
      minWidth={320}
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      dragHandleSelector=".ws-drag-trigger"
      onKeyDown={handleKeyDown}
      fullscreen={bowser.mobile}
      className="ws-link-edit-modal"
      attributes={modalAttributes}
      overlay={overlay}
      centered={centered}
    >
      <PanelGroup height="100%" style={{ flexShrink: '0' }} className="ws-link-edit-modal__panel">
        <Panel
          theme="gray-100"
          corners="squared"
          align="middle"
          height={PANEL_HEIGHT}
          style={{ flexShrink: '0' }}
          className="ws-drag-trigger ws-modal__content"
        >
          <Heading type="heading-6" singleLine>
            {captions.dialogTitle}
          </Heading>
        </Panel>
        <Panel
          theme="white"
          className={classNames('ws-link-edit-modal__panel', {
            'ws-link-edit-modal__panel--mobile': bowser.mobile,
          })}
          height={panelHeight}
        >
          <div className="ws-modal__content">
            {linkEditorJSX}

            {showTitle && (
              <div className="ws-modal__block">
                <Stack gap="spacing-3-xs">
                  <ControlGroup title={captions.title}>
                    <InputGroupField
                      placeholder={i18next.t('Enter item name')}
                      name={FIELD_NAMES.title}
                      control={control}
                      rules={{
                        validate: (value) =>
                          value?.trim() ? null : i18next.t('Item Name should not be empty'),
                      }}
                      onClick={() => onInputClick(INPUTS_IDS.titleInput)}
                    />
                    {!errors[FIELD_NAMES.title] && <FieldStatus text={captions.titleAnnotation} />}
                  </ControlGroup>
                </Stack>
              </div>
            )}

            {showDeleteButton && (
              <div className="ws-modal__block">
                <ActionButton label={i18next.t('Delete link')} onClick={handleDeleteLink} />
              </div>
            )}
          </div>
        </Panel>
        <Panel theme="white" style={{ flexShrink: '0' }} height={48}>
          <DialogButtonGroup>
            <DialogButton disabled={isLinkSelectorDisabled} onClick={manageSubmit(onSubmit)}>
              {captions.save}
            </DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{i18next.t('Cancel')}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

LinkSettingDialog.propTypes = {
  dialogPosition: PropTypes.shape({
    offsetX: PropTypes.number,
    offsetY: PropTypes.number,
  }),
  captions: PropTypes.shape().isRequired,
  latestAnchorReference: PropTypes.string,
  title: PropTypes.string,
  modalAttributes: PropTypes.shape({}),
  linkSelectorOptions: PropTypes.shape({ linkTypeOptions: PropTypes.string }).isRequired,
  linkSelectorCaptions: PropTypes.shape({}).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  showTitle: PropTypes.bool,
  showDeleteButton: PropTypes.bool,
  handleDeleteLink: PropTypes.func,
  handleLinkChange: PropTypes.func,
  handleTargetBlankChange: PropTypes.func,
  handleNoFollowChange: PropTypes.func,
  handleUploadFile: PropTypes.func,
  validateLink: PropTypes.func,
  panelHeight: PropTypes.string,
  setIsOverlayClicked: PropTypes.func,
  overlay: PropTypes.oneOf(['transparent', 'visible', 'none']),
  centered: PropTypes.bool,
  appearance: PropTypes.oneOf([
    constants.common.DIALOG_APPEARANCE,
    constants.common.DIALOG_BLOCK_APPEARANCE,
  ]),
  isExternalLinksDisabled: PropTypes.bool,
  isExternalLinksUnresolved: PropTypes.bool,
  onUpgradeLinksClick: PropTypes.func,
  onInputClick: PropTypes.func,
  handleDownloadOption: PropTypes.func,
  customPathIsLoading: PropTypes.bool,
  customPathDisabled: PropTypes.bool,
  toggleCustomPathField: PropTypes.func,
  onCustomPathChange: PropTypes.func,
  pathValidation: PropTypes.func,
};

export default LinkSettingDialog;
